import React from 'react';
import { Text, View } from 'react-native';
import { HelperText, TextInput } from 'react-native-paper';
import {
  formatNumericStringWithCommas,
  sanitizeMoneyInput,
  sanitizeNumericInput,
} from '../../../../utils/libs';
import type { ProductFormData, ProductFormErrors } from '../productFormTypes';
import { useProductFormFieldTheme } from './productFormStyles';
import ProductUnitAffix from './ProductUnitAffix';

/**
 * Simple (non-variant) product fields extracted verbatim from NewProduct.tsx
 * (US3, T053): Product Details name/category plus Pricing and Inventory.
 * The wizard's simplified presentation and the full NewProduct screen render
 * these same components so "More options" never duplicates fields.
 */

export interface ProductDetailsFieldsProps {
  formData: Pick<ProductFormData, 'productname' | 'category'>;
  errors: ProductFormErrors;
  onChangeField: (field: string, value: string) => void;
  onOpenCategoryPicker: () => void;
  /** Section heading is optional so hosts can own their own headings. */
  showSectionTitle?: boolean;
  /** "Service Name *" on the Service segment. */
  nameLabel?: string;
  /** Deterministic visual focus for non-interactive renderers such as Remotion. */
  focusedField?: 'name' | 'category';
}

export const ProductDetailsFields: React.FC<ProductDetailsFieldsProps> = ({
  formData,
  errors,
  onChangeField,
  onOpenCategoryPicker,
  showSectionTitle = true,
  nameLabel = 'Product Name *',
  focusedField,
}) => {
  const { theme, styles, inputOutlineProps, webPlaceholderTheme } =
    useProductFormFieldTheme();

  // A fragment, so the host card's gap spaces the title and fields.
  return (
    <>
      {showSectionTitle ? (
        <Text style={styles.sectionTitle}>Product Details</Text>
      ) : null}
      <View style={styles.field}>
        <TextInput
          mode="outlined"
          label={nameLabel}
          value={formData.productname}
          onChangeText={t => onChangeField('productname', t)}
          placeholderTextColor={theme.colors.textSecondary}
          {...inputOutlineProps}
          outlineColor={
            focusedField === 'name'
              ? theme.colors.primary
              : inputOutlineProps.outlineColor
          }
          {...webPlaceholderTheme}
          style={styles.input}
          error={!!errors.productname}
          testID="product-name-input"
        />
      </View>
      <View style={styles.field}>
        <TextInput
          mode="outlined"
          label="Category *"
          value={formData.category}
          onChangeText={t => onChangeField('category', t)}
          placeholderTextColor={theme.colors.textSecondary}
          {...inputOutlineProps}
          outlineColor={
            focusedField === 'category'
              ? theme.colors.primary
              : inputOutlineProps.outlineColor
          }
          {...webPlaceholderTheme}
          style={styles.input}
          right={
            <TextInput.Icon
              icon="chevron-down"
              color={theme.colors.primaryDark}
              onPress={onOpenCategoryPicker}
            />
          }
          error={!!errors.category}
          testID="product-category-input"
        />
      </View>
    </>
  );
};

export interface SimplePricingInventoryFieldsProps {
  formData: Pick<
    ProductFormData,
    'buyingprice' | 'sellingprice' | 'quantity' | 'msl'
  >;
  errors: ProductFormErrors;
  priceType: string;
  unitofmass?: string;
  onChangeField: (field: string, value: string) => void;
  /** The wizard's simplified presentation hides MSL until More options. */
  showMinimumStockLevel?: boolean;
  showSectionTitles?: boolean;
  /** Deterministic visual focus for non-interactive renderers such as Remotion. */
  focusedField?: 'buy' | 'sell' | 'qty' | 'msl';
}

export const SimplePricingInventoryFields: React.FC<
  SimplePricingInventoryFieldsProps
> = ({
  formData,
  errors,
  priceType,
  unitofmass,
  onChangeField,
  showMinimumStockLevel = true,
  showSectionTitles = true,
  focusedField,
}) => {
  const { theme, styles, inputOutlineProps, webPlaceholderTheme } =
    useProductFormFieldTheme();

  return (
    <>
      {showSectionTitles ? (
        <Text style={[styles.sectionTitle, styles.groupStart]}>Pricing</Text>
      ) : null}
      <View style={styles.fieldRow}>
        <View style={styles.fieldHalf}>
          <TextInput
            mode="outlined"
            label="Buying Price *"
            value={formatNumericStringWithCommas(formData.buyingprice)}
            onChangeText={t =>
              onChangeField(
                'buyingprice',
                sanitizeMoneyInput(t),
              )
            }
            keyboardType="numeric"
            placeholderTextColor={theme.colors.textSecondary}
            {...inputOutlineProps}
            outlineColor={
              focusedField === 'buy'
                ? theme.colors.primary
                : inputOutlineProps.outlineColor
            }
            {...webPlaceholderTheme}
            style={styles.input}
            error={!!errors.buyingprice}
            testID="product-buying-price-input"
            right={ProductUnitAffix({
              priceType,
              unitOfMass: unitofmass,
              kind: 'price',
            })}
          />
        </View>
        <View style={styles.fieldHalf}>
          <TextInput
            mode="outlined"
            label="Selling Price *"
            value={formatNumericStringWithCommas(formData.sellingprice)}
            onChangeText={t =>
              onChangeField(
                'sellingprice',
                sanitizeMoneyInput(t),
              )
            }
            keyboardType="numeric"
            placeholderTextColor={theme.colors.textSecondary}
            {...inputOutlineProps}
            outlineColor={
              focusedField === 'sell'
                ? theme.colors.primary
                : inputOutlineProps.outlineColor
            }
            {...webPlaceholderTheme}
            style={styles.input}
            error={!!errors.sellingprice}
            testID="product-selling-price-input"
            right={ProductUnitAffix({
              priceType,
              unitOfMass: unitofmass,
              kind: 'price',
            })}
          />
        </View>
      </View>

      {showSectionTitles ? (
        <Text style={[styles.sectionTitle, styles.groupStart]}>Inventory</Text>
      ) : null}
      <View style={styles.fieldRow}>
        <View style={styles.fieldHalf}>
          <TextInput
            mode="outlined"
            label="Quantity *"
            value={formatNumericStringWithCommas(formData.quantity)}
            onChangeText={t =>
              onChangeField(
                'quantity',
                sanitizeNumericInput(t, { isUnit: priceType === 'unit' }),
              )
            }
            keyboardType="numeric"
            placeholderTextColor={theme.colors.textSecondary}
            {...inputOutlineProps}
            outlineColor={
              focusedField === 'qty'
                ? theme.colors.primary
                : inputOutlineProps.outlineColor
            }
            {...webPlaceholderTheme}
            style={styles.input}
            error={!!errors.quantity}
            testID="product-quantity-input"
            right={ProductUnitAffix({
              priceType,
              unitOfMass: unitofmass,
              kind: 'stock',
            })}
          />
        </View>
        {showMinimumStockLevel ? (
          <View style={styles.fieldHalf}>
            <TextInput
              mode="outlined"
              label="Minimum Stock Level"
              value={formatNumericStringWithCommas(formData.msl)}
              onChangeText={t =>
                onChangeField(
                  'msl',
                  sanitizeNumericInput(t, { isUnit: priceType === 'unit' }),
                )
              }
              keyboardType="numeric"
              placeholderTextColor={theme.colors.textSecondary}
              {...inputOutlineProps}
              outlineColor={
                focusedField === 'msl'
                  ? theme.colors.primary
                  : inputOutlineProps.outlineColor
              }
              {...webPlaceholderTheme}
              style={styles.input}
              error={false}
              testID="product-msl-input"
              right={ProductUnitAffix({
                priceType,
                unitOfMass: unitofmass,
                kind: 'stock',
              })}
            />
          </View>
        ) : null}
      </View>
      {showMinimumStockLevel &&
      formData.msl &&
      formData.quantity &&
      Number(formData.quantity) <= Number(formData.msl) ? (
        <HelperText type="info" visible>
          This product will start as low stock
        </HelperText>
      ) : null}
    </>
  );
};
